import {IValue, IValueConstructor} from '@mintdata/mint-kit/api/types/Value';
import {frameModelSchema} from '@mintdata/mint-kit/impl/util/serializr/src/api/getDefaultModelSchema';

export type TypeName = string;

export class Type
{
    public static readonly Boolean = new Type('boolean');
    public static readonly Byte = new Type('byte');
    public static readonly Integer = new Type('integer');
    public static readonly Number = new Type('number');
    public static readonly String = new Type('string');

    public static readonly Currency = new Type('currency');
    public static readonly Date = new Type('date');
    public static readonly DateTime = new Type('datetime');
    public static readonly Time = new Type('time');

    public static readonly StringEnum = new Type('stringEnum');
    public static readonly IntegerEnum = new Type('integerEnum');

    public static readonly BinaryData = new Type('binaryData');

    public static readonly Frame = new Type('frame');
    public static readonly Error = new Type('error');
    public static readonly Empty = new Type('empty');

    public static All =
        [
            Type.Boolean,
            Type.Byte,
            Type.Integer,
            Type.Number,
            Type.String,

            Type.Currency,
            Type.Date,
            Type.DateTime,
            Type.Time,

            Type.StringEnum,
            Type.IntegerEnum,

            Type.BinaryData,

            Type.Frame,
            Type.Error,
            Type.Empty
        ];

    private _kind:string;

    private constructor(kind:string)
    {
        this._kind = kind;
    }

    public get kind():string
    {
        return this._kind;
    }

    public isEqualTo(other:Type):boolean
    {
        return other && other._kind && other._kind.toLowerCase() === this._kind.toLowerCase();
    }

    public toString():string
    {
        return this._kind.toLowerCase();
    }

    public static fromTypeName(type:TypeName):Type | null
    {
        if( !type )
            throw new Error(`Type.fromTypeName(): type argument must be defined`);

        if( type && type.toLowerCase() === 'precisenumber' ) // TODO: Implement precisenumber Type
            return Type.Number;

        for( let aType of Type.All )
        {
            if( aType._kind.toLowerCase() === type.toLowerCase() )
                return aType;
        }

        return null;
    }

    public static isError(object:any):boolean
    {
        return Type.isInstanceOf(object, Type.Error);
    }

    public static isInstanceOf(object:any, type:Type):boolean
    {
        if( !object || !type )
            return false;

        return typeof object.getType === 'function'
            && type.isEqualTo(object.getType());
    }

    public static isInstanceOfIValue(object:any):boolean
    {
        if( !object || typeof object.getType !== 'function' )
            return false;

        const type:Type = object.getType();

        for( let aType of Type.All )
        {
            if( aType.isEqualTo(type) )
                return true;
        }

        return false;
    }

    public static hasToIValue(object:any):boolean
    {
        if( !object )
            return false;

        return typeof object.toIValue === 'function';
    }

    public static readonly TYPE_KEY = '__type';

    private static getTypeNameToValueCtorMap():{[key:string]:IValueConstructor}
    {
        const ABoolean = require('@mintdata/mint-kit/api/values/ABoolean').ABoolean;
        const AByte = require('@mintdata/mint-kit/api/values/AByte').AByte;
        const AInteger = require('@mintdata/mint-kit/api/values/AInteger').AInteger;
        const ANumber = require('@mintdata/mint-kit/api/values/ANumber').ANumber;
        const AString = require('@mintdata/mint-kit/api/values/AString').AString;

        const ACurrency = require('@mintdata/mint-kit/api/values/ACurrency').ACurrency;
        const ADate = require('@mintdata/mint-kit/api/values/ADate').ADate;
        const ADateTime = require('@mintdata/mint-kit/api/values/ADateTime').ADateTime;
        const ATime = require('@mintdata/mint-kit/api/values/ATime').ATime;

        const AStringEnum = require('@mintdata/mint-kit/api/values/AStringEnum').AStringEnum;
        const AIntegerEnum = require('@mintdata/mint-kit/api/values/AIntegerEnum').AIntegerEnum;

        const ABinaryData = require('@mintdata/mint-kit/api/values/ABinaryData').ABinaryData;

        const AFrame = require('@mintdata/mint-kit/api/values/frames/AFrame').AFrame;
        const AError = require('@mintdata/mint-kit/api/values/AError').AError;
        const AEmpty = require('@mintdata/mint-kit/api/values/AEmpty').AEmpty;

        return {
            [Type.Boolean.kind.toLowerCase()]: ABoolean,
            [Type.Byte.kind.toLowerCase()]: AByte,
            [Type.Integer.kind.toLowerCase()]: AInteger,
            [Type.Number.kind.toLowerCase()]: ANumber,
            [Type.String.kind.toLowerCase()]: AString,

            [Type.Currency.kind.toLowerCase()]: ACurrency,
            [Type.Date.kind.toLowerCase()]: ADate,
            [Type.DateTime.kind.toLowerCase()]: ADateTime,
            [Type.Time.kind.toLowerCase()]: ATime,

            [Type.StringEnum.kind.toLowerCase()]: AStringEnum,
            [Type.IntegerEnum.kind.toLowerCase()]: AIntegerEnum,

            [Type.BinaryData.kind.toLowerCase()]: ABinaryData,

            precisenumber: ANumber, // TODO: Implement precisenumber Type

            [Type.Frame.kind.toLowerCase()]: AFrame,
            [Type.Error.kind.toLowerCase()]: AError,
            [Type.Empty.kind.toLowerCase()]: AEmpty
        };
    }

    public static valueCtorFromType(type:Type):IValueConstructor
    {
        return Type.getTypeNameToValueCtorMap()[type.kind.toLowerCase()];
    }

    public static deserializeIValue(iValueJson:any):IValue
    {
        const {deserialize} = require('@mintdata/mint-kit/impl/util/serializr/src/serializr');
        const AError = require('@mintdata/mint-kit/api/values/AError').AError;
        const AEmpty = require('@mintdata/mint-kit/api/values/AEmpty').AEmpty;

        let deserializedValue:IValue;

        try
        {
            if( AError.isSerializedErrorValue(iValueJson) )
                deserializedValue = deserialize(AError, iValueJson);
            else
            {
                const fieldType:Type = Type.fromTypeName(iValueJson[Type.TYPE_KEY]);
                const valueCtor = Type.valueCtorFromType(fieldType);

                if( AEmpty.isSerializedEmptyValue(iValueJson) )
                    deserializedValue = AEmpty.Value;
                else
                {
                    const schema = fieldType.isEqualTo(Type.Frame)
                        ? frameModelSchema
                        : valueCtor;

                    deserializedValue = deserialize(schema, iValueJson);
                }
            }
        }
        catch( error )
        {
            deserializedValue = AError.fromString('#InvalidIValueJson!');
        }

        return deserializedValue;
    }
}
